import React, { useCallback, useMemo, useState } from "react";
import { Text, View } from "react-native";
import {
  TouchableHighlight,
  TouchableOpacity,
} from "react-native-gesture-handler";
import colors from "@constants/color";
import t from "@constants/localization";
import metrics from "../../../constants/metrics";
import { getData, setData } from "../../../lib/storage";
import { submitPoll } from "../../../services/api";

const PATH = "screens/Story";

const DEFAULT_FIVE_RATINGS = [
  "#EA3532",
  "#FF6443",
  "#FFA62A",
  "#E3C400",
  "#9DCB00",
  "#32AF00",
];
const DEFAULT_TEN_RATINGS = [
  "#EA3532",
  "#F74445",
  "#FF6443",
  "#FF8538",
  "#FFA62A",
  "#FFC100",
  "#E3C400",
  "#C0C900",
  "#9DCB00",
  "#79CA00",
  "#32AF00",
];

const RatingPoll = (props) => {
  const {
    id = "",
    header = "",
    header_color = "white",
    question = "",
    question_color = "",
    answers = [
      {
        answer: "",
        answer_color: "green",
      },
      {
        answer: "",
        answer_color: "green",
      },
    ],

    pauseStory = () => {},
    unpauseStory = () => {},
    submitted,
    setSubmitted,
    trackEvent = () => {},
  } = props;

  const [selectedAnswerId, setSelectedAnswerId] = useState();
  const onSubmit = useCallback(() => {
    trackEvent({ pollId: id, answerId: selectedAnswerId });
    submitPoll({ pollId: id, answerId: selectedAnswerId })
      .then(async () => {
        setSubmitted((s) => [...s, id]);
        const submittedPolls = (await getData("cmsSubmittedPolls")) ?? [];
        await setData("cmsSubmittedPolls", [...submittedPolls, id]);
      })
      .catch((err) => console.log(err));
  }, [id, selectedAnswerId, setSubmitted]);

  const sortedAnswers = useMemo(
    () => [...answers].sort((a, b) => a.id - b.id),

    [answers],
  );
  return (
    <View
      style={{
        flex: 1,
        alignSelf: "center",
        position: "absolute",
        zIndex: 10,
        width: metrics.screenWidth - 30,
        left: 15,
      }}
    >
      <Text
        style={{
          fontFamily: "NotoSansDevanagari-Bold",
          color: header_color ?? "white",
          fontSize: 20,
          textAlign: "center",
          marginBottom: 5,
        }}
      >
        {header}
      </Text>

      <Text
        style={{
          color: question_color ?? "white",
          fontSize: 12,
          textAlign: "center",
          marginBottom: 10,
        }}
      >
        {question}
      </Text>
      <View
        style={{
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "stretch",
          position: "relative",
          marginTop: 40,
          zIndex: 10,
        }}
      >
        <RatingMeter
          answers={sortedAnswers}
          {...{
            submitted,
            selectedAnswerId,
            pauseStory,
            setSelectedAnswerId,
            unpauseStory,
          }}
        />
      </View>

      {!submitted ? (
        <>
          <View style={{ height: 64 }} />
          <TouchableOpacity
            onPress={onSubmit}
            disabled={!selectedAnswerId}
            style={{
              backgroundColor: selectedAnswerId ? colors.primary : "#909090",
              borderRadius: 6,
            }}
          >
            <Text
              style={{
                fontFamily: "NotoSansDevanagari-Bold",
                fontSize: 20,
                color: "white",
                textAlign: "center",
                paddingVertical: 15,
                elevation: 1,
                opacity: selectedAnswerId ? 1 : 0.36,
              }}
            >
              {t(`${PATH}/submit`)}
            </Text>
          </TouchableOpacity>
        </>
      ) : (
        <View
          style={{
            backgroundColor: "white",
            padding: 20,
            borderRadius: 10,
            marginBottom: 6,
            marginTop: 64,
          }}
        >
          <Text
            style={{
              textAlign: "center",
              fontSize: 16,
              opacity: 0.56,
            }}
          >
            {t(`${PATH}/youHaveAnswered`)}
          </Text>
        </View>
      )}
    </View>
  );
};

const RatingMeter = (props) => {
  const {
    answers,
    submitted,
    selectedAnswerId,
    pauseStory,
    setSelectedAnswerId,
    unpauseStory,
  } = props;

  const cardWidth = useMemo(
    () => (metrics.screenWidth - metrics.screenWidth / 15) / answers.length,
    [answers],
  );
  if (!Array.isArray(answers)) return null;
  if (answers && !answers.length) return null;
  const defaultColors =
    answers.length === 6 ? DEFAULT_FIVE_RATINGS : DEFAULT_TEN_RATINGS;

  return answers.map(
    (
      { answer, answer_color, id: answerId, description, description_color },
      index,
    ) => (
      <>
        <TouchableHighlight
          underlayColor="#fff"
          key={answerId}
          activeOpacity={0.9}
          disabled={submitted}
          onPressIn={pauseStory}
          onPressOut={unpauseStory}
          onPress={() => void setSelectedAnswerId(answerId)}
          style={{
            backgroundColor:
              answerId === selectedAnswerId
                ? "white"
                : answer_color || defaultColors[index],
            marginBottom: 6,
            width: cardWidth,
            borderTopLeftRadius: index === 0 ? 6 : 0,
            borderBottomLeftRadius: index === 0 ? 6 : 0,
            borderTopRightRadius: index === answers.length - 1 ? 6 : 0,
            borderBottomRightRadius: index === answers.length - 1 ? 6 : 0,
            borderRightWidth: index !== answers.length - 1 ? 0.5 : 0,
            borderColor: "#383838",
            opacity: submitted ? (selectedAnswerId === answerId ? 1 : 0.5) : 1,
          }}
        >
          <Text
            style={{
              textAlign: "center",
              textTransform: "uppercase",
              fontFamily: "Muli-SemiBold",
              color: selectedAnswerId === answerId ? "black" : "white",
              paddingVertical: 14,
              fontSize: 16,
            }}
          >
            {answer}
          </Text>
        </TouchableHighlight>
        <Text
          style={{
            position: "absolute",
            color: description_color,
            top: "100%",
            width: metrics.screenWidth - metrics.screenWidth / 15,
            paddingLeft: index < answers.length / 2 ? cardWidth * index + 1 : 0,
            paddingRight:
              index < answer.length / 2
                ? 0
                : (answers.length - 1 - index) * cardWidth,
            textAlign:
              index < answers.length / 2
                ? "left"
                : answers.length / 2 === index
                  ? "center"
                  : "right",
          }}
        >
          {description?.trim() ?? ""}
        </Text>
      </>
    ),
  );
};
export default RatingPoll;
